import { errorToMessage } from "../../utils/errors"
import * as hourReportApi from "../api"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"

const {
  useCreateHourReportMutation,
  useSetDaysReadyForCheckMutation,
  useSetDaysReadyForApprovalMutation,
  useSetDaysApprovedMutation,
  useUpdateHourReportInfoMutation,
  useUpdateHourReportProjectMutation,
  useUpdateHourReportTaskMutation,
  useUpdateHourReportHoursMutation,
  useCheckHourReportMutation,
  useApproveHourReportMutation,
  usePerformHourReportAdminActionMutation,
  usePerformHourReportDayAuditAdminActionMutation,
  useRequestHourReportReadyForCheckMutation,
  useRequestHourReportReadyForApprovalMutation,
  useDeleteHourReportMutation,
} = hourReportApi

export const useHourReportMutations = ({ supportsChecking = true }: UseHourReportMutationsOptions = {}) => {
  const translations = useTranslations()
  const { enqueueSnackbar } = useSnackbar()
  const [createHourReportMutation, createHourReportState] = useCreateHourReportMutation()
  const [updateHourReportInfoMutation, updateHourReportInfoState] = useUpdateHourReportInfoMutation()
  const [updateHourReportProjectMutation, updateHourReportProjectState] = useUpdateHourReportProjectMutation()
  const [updateHourReportTaskMutation, updateHourReportTaskState] = useUpdateHourReportTaskMutation()
  const [updateHourReportHoursMutation, updateHourReportHoursState] = useUpdateHourReportHoursMutation()
  const [checkHourReportMutation, checkHourreportState] = useCheckHourReportMutation()
  const [approveHourReportMutation, approveHourReportState] = useApproveHourReportMutation()
  const [performHourReportAdminActionMutation, performHourReportAdminActionState] =
    usePerformHourReportAdminActionMutation()
  const [performHourReportDayAuditAdminActionMutation, performHourReportDayAuditAdminActionState] =
    usePerformHourReportDayAuditAdminActionMutation()
  const [requestHourReportReadyForCheckMutation, requestHourReportReadyForCheckState] =
    useRequestHourReportReadyForCheckMutation()
  const [requestHourReportReadyForApprovalMutation, requestHourReportReadyForApprovalState] =
    useRequestHourReportReadyForApprovalMutation()
  const [deleteHourReportMutation, deleteHourReportState] = useDeleteHourReportMutation()
  const [setDaysReadyForCheckMutation, setDaysReadyForCheckMutationState] = useSetDaysReadyForCheckMutation()
  const [setDaysReadyForApprovalMutation, setDaysReadyForApprovalMutationState] = useSetDaysReadyForApprovalMutation()
  const [setDaysApprovedMutation, setDaysApprovedMutationState] = useSetDaysApprovedMutation()

  const showErrorMessage = (error: unknown) => {
    enqueueSnackbar(errorToMessage(error, translations), { variant: "error" })
  }

  const showSuccessMessage = (message: string) => {
    enqueueSnackbar(message, { variant: "success" })
  }

  const createHourReport = async (data: hourReportApi.NewHourReportData) => {
    return await createHourReportMutation(data)
      .unwrap()
      .then(({ id }) => {
        showSuccessMessage(translations.hourReportSaved)
        return id
      })
      .catch((error) => {
        showErrorMessage(error)
        return null
      })
  }

  const updateHourReportInfo = async (id: string, data: hourReportApi.HourReportInfoUpdateData) => {
    return await updateHourReportInfoMutation({ id, ...data })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const updateHourReportProject = async (id: string, data: hourReportApi.HourReportProjectUpdateData) => {
    return await updateHourReportProjectMutation({ id, ...data })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const updateHourReportTask = async (id: string, data: hourReportApi.HourReportTaskUpdateData) => {
    return await updateHourReportTaskMutation({ id, ...data })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const updateHourReportHours = async (id: string, data: hourReportApi.HourReportHoursUpdateData) => {
    return await updateHourReportHoursMutation({ id, ...data })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const checkHourReport = async (id: string, data: hourReportApi.HourReportCheckData) => {
    return await checkHourReportMutation({ id, ...data })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const approveHourReport = async (id: string, data: hourReportApi.HourReportApproveData) => {
    return await approveHourReportMutation({ id, ...data })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const performHourReportAdminAction = async (id: string, data: hourReportApi.HourReportAdminActionData) => {
    return await performHourReportAdminActionMutation({ id, ...data })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const performHourReportDayAuditAdminAction = async (data: hourReportApi.HourReportDayAuditAdminActionData) => {
    return await performHourReportDayAuditAdminActionMutation(data).unwrap().catch(showErrorMessage)
  }

  const requestReadyForCheck = async (id: string) => {
    return await requestHourReportReadyForCheckMutation({ id })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        return false
      })
  }

  const requestReadyForApproval = async (id: string) => {
    return await requestHourReportReadyForApprovalMutation({ id })
      .unwrap()
      .then(() => true)
      .catch((error) => {
        return false
      })
  }

  const deleteHourReport = async (id: string, as: hourReportApi.HourReportRole) => {
    return await deleteHourReportMutation({ id, as })
      .unwrap()
      .then(() => showSuccessMessage(translations.hourReportDeleteSuccess))
      .catch(showErrorMessage)
  }

  const setDaysReadyForCheck = async (data: hourReportApi.AuditDayReadyForCheckData) => {
    return await setDaysReadyForCheckMutation(data)
      .unwrap()
      .then((response) => {
        if (data.status === "submitted") {
          const successMessage = supportsChecking ? translations.allReadyForReview : translations.allReadyForApproval
          const notificationSentMessage = supportsChecking
            ? translations.checkerNotified
            : translations.approverNotified
          showSuccessMessage(response.notificationSent ? notificationSentMessage : successMessage)
        }
        return true
      })
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const setDaysReadyForApproval = async (data: hourReportApi.AuditDayReadyForApprovalData) => {
    return await setDaysReadyForApprovalMutation(data)
      .unwrap()
      .then((response) => {
        if (data.status === "checked") {
          const message = response.notificationSent ? translations.approverNotified : translations.allReadyForApproval
          showSuccessMessage(message)
        }
        return true
      })
      .catch((error) => {
        showErrorMessage(error)
        return false
      })
  }

  const setDaysApproved = async (data: hourReportApi.AuditDayApprovedData) => {
    return await setDaysApprovedMutation(data)
      .unwrap()
      .catch((error) => {
        showErrorMessage(error)
      })
  }

  return {
    createHourReport,
    setDaysReadyForCheck,
    setDaysReadyForApproval,
    setDaysApproved,
    updateHourReportInfo,
    updateHourReportProject,
    updateHourReportTask,
    updateHourReportHours,
    checkHourReport,
    approveHourReport,
    performHourReportAdminAction,
    performHourReportDayAuditAdminAction,
    requestReadyForCheck,
    requestReadyForApproval,
    deleteHourReport,
    meta: {
      createHourReportState,
      setDaysReadyForCheckMutationState,
      setDaysReadyForApprovalMutationState,
      setDaysApprovedMutationState,
      updateHourReportInfoState,
      updateHourReportProjectState,
      updateHourReportTaskState,
      updateHourReportHoursState,
      checkHourreportState,
      approveHourReportState,
      performHourReportAdminActionState,
      performHourReportDayAuditAdminActionState,
      requestHourReportReadyForCheckState,
      requestHourReportReadyForApprovalState,
      deleteHourReportState,
    },
  }
}

type UseHourReportMutationsOptions = {
  supportsChecking?: boolean
}

const defaultTranslations = {
  allReadyForReview: "All time entries are ready for review.",
  allReadyForApproval: "All time entries are ready for approval.",
  checkerNotified: "Reviewer has been notified that all time entries are ready for review.",
  approverNotified: "Approver has been notified that all time entries are ready for approval.",
  hourReportCreationFailed: "Failed to save hour report",
  hourReportSaved: "Hour report saved successfully",
  hourReportDeleteSuccess: "Hour report deleted successfully",
  hourReportInvalidStateTransition: "Invalid state transition",
  hourReportTaskAndProjectMismatch: "Task does not belong to project",
  hourReportUnknownMethod: "Unknown method",
  hourReportInvalidFieldUpdate: "Cannot update field for reporting method",
  hourReportHoursUpdateRequiresManualReport: "Only manual hour reports can have hours updated directly.",
  hourReportTimeRangeUpdateRequiresTimedReport: "Manual hour reports cannot have start and end times updated.",
  hourReportTaskAssignmentRequiresProject: "A task cannot be assigned without a project.",
  hourReportReportAlreadyReadyCheck: "This hour report is already ready for check.",
  hourReportReportReadyApprovalCannotMoveToReadyCheck: "A checked hour report cannot be marked ready for check.",
  hourReportReportApprovedCannotMoveToReadyCheck: "An approved hour report cannot be moved back to ready for check.",
  hourReportReportAlreadyDraft: "This hour report is already a draft.",
  hourReportReportReadyApprovalCannotMoveToDraft: "A checked hour report cannot be moved back to draft.",
  hourReportReportApprovedCannotMoveToDraft: "An approved hour report cannot be moved back to draft.",
  hourReportReportDraftCannotBeChecked: "Draft hour reports cannot be checked.",
  hourReportReportApprovedCannotBeChecked: "Approved hour reports cannot be checked again.",
  hourReportReportDraftCannotBeUnchecked: "Draft hour reports have not been checked, so they cannot be unchecked.",
  hourReportReportReadyCheckCannotBeUnchecked: "This hour report has not been checked yet.",
  hourReportReportApprovedCannotBeUnchecked: "Approved hour reports cannot be unchecked.",
  hourReportReportDraftCannotBeApproved: "Draft hour reports cannot be approved.",
  hourReportReportReadyCheckCannotBeApproved: "Hour reports ready for check cannot be approved.",
  hourReportReportAlreadyApproved: "This hour report is already approved.",
  hourReportReportDraftCannotBeUnapproved: "Draft hour reports are not approved, so they cannot be unapproved.",
  hourReportReportReadyCheckCannotBeUnapproved:
    "Hour reports ready for check are not approved, so they cannot be unapproved.",
  hourReportReportReadyApprovalCannotBeUnapproved:
    "Checked hour reports are not approved yet, so they cannot be unapproved.",
  hourReportDayAlreadyReadyCheck: "This day is already ready for check.",
  hourReportDayReadyApprovalCannotMoveToReadyCheck: "A checked day cannot be marked ready for check.",
  hourReportDayApprovedCannotMoveToReadyCheck: "An approved day cannot be moved back to ready for check.",
  hourReportDayAlreadyNotReadyCheck: "This day is already not ready for check.",
  hourReportDayReadyApprovalCannotMoveToNotReadyCheck: "A checked day cannot be moved back to not ready for check.",
  hourReportDayApprovedCannotMoveToNotReadyCheck: "An approved day cannot be moved back to not ready for check.",
  hourReportDayNotReadyCheckCannotBeChecked: "Days that are not ready for check cannot be checked.",
  hourReportDayApprovedCannotBeChecked: "Approved days cannot be checked again.",
  hourReportDayNotReadyCheckCannotBeUnchecked:
    "Days that are not ready for check have not been checked, so they cannot be unchecked.",
  hourReportDayReadyCheckCannotBeUnchecked: "This day has not been checked yet.",
  hourReportDayApprovedCannotBeUnchecked: "Approved days cannot be unchecked.",
  hourReportDayNotReadyCheckCannotBeApproved: "Days that are not ready for check cannot be approved.",
  hourReportDayReadyCheckCannotBeApproved: "Days ready for check cannot be approved.",
  hourReportDayAlreadyApproved: "This day is already approved.",
  hourReportDayNotReadyCheckCannotBeUnapproved:
    "Days that are not ready for check are not approved, so they cannot be unapproved.",
  hourReportDayReadyCheckCannotBeUnapproved: "Days ready for check are not approved, so they cannot be unapproved.",
  hourReportDayReadyApprovalCannotBeUnapproved: "Checked days are not approved yet, so they cannot be unapproved.",
  hourReportUseCaseInvalidAction: "This hour report action is not supported.",
  hourReportUseCaseTaskProjectMismatch: "The selected task does not belong to the selected project.",
  hourReportUseCaseDraftReportCannotBeChecked: "Draft hour reports cannot be checked.",
  hourReportUseCaseApprovedReportCannotBeChecked: "Approved hour reports cannot be checked.",
  hourReportUseCaseDraftReportCannotBeEditedDuringApproval: "Draft hour reports cannot be edited during approval.",
  hourReportUseCaseReadyCheckReportCannotBeEditedDuringApproval:
    "Hour reports ready for check cannot be edited during approval.",
  hourReportUseCaseArchivedTaskCannotReportHours: "Hours cannot be reported to an archived task.",
  hourReportUseCaseRejectedTaskCannotReportHours: "Hours cannot be reported to a rejected task.",
  hourReportUseCaseArchivedProjectCannotReportHours: "Hours cannot be reported to an archived project.",
  hourReportUseCaseRejectedProjectCannotReportHours: "Hours cannot be reported to a rejected project.",
  hourReportUseCaseCheckConflict: "This hour report changed while it was being checked.",
  hourReportServiceUserRequired: "User is required in AuthContext",
  hourReportServiceUserProjectExpected: "Project expected to be user project",
  hourReportServiceUserTaskExpected: "Task expected to be user task",
  hourReportServiceMembershipExpected: "Membership is expected in organisation hour report context",
  hourReportServiceOrgProjectExpected: "Project expected to be organisation project",
  hourReportServiceOrgTaskExpected: "Task expected to be organisation task",
  hourReportServiceStrategyNotFound: "No auth strategy found",
  hourReportServiceInvalidAuthRequestType: "Invalid auth request type for strategy",
  hourReportServiceContextDataMissing: "Required context data is missing for authorization strategy",
  hourReportReporterCannotChangeReadyForCheckHourReport: "You cannot change an hour report that is ready for check.",
  hourReportReporterCannotChangeCheckedHourReport: "This report has been checked and cannot be changed.",
  hourReportReporterCannotChangeApprovedHourReport: "This report has been approved and cannot be changed.",
  hourReportReporterCannotDelete: "You can only delete draft hour reports.",
  hourReportDraftCannotBeChecked: "Draft hour reports cannot be checked.",
  hourReportApprovedCannotBeChecked: "Approved hour reports cannot be checked.",
  hourReportDraftCannotBeEditedDuringApproval: "Draft hour reports cannot be edited during approval.",
  hourReportReadyCheckCannotBeEditedDuringApproval: "Hour reports ready for check cannot be edited during approval.",
  hourReportReadyApprovalRequestRequiresReadyForCheck:
    "Only hour reports ready for check can be requested ready for approval.",
  hourReportArchivedTaskCannotReportHours: "Hours cannot be reported to an archived task.",
  hourReportRejectedTaskCannotReportHours: "Hours cannot be reported to a rejected task.",
  hourReportCompletedTaskCannotReportHours: "Hours cannot be reported to a completed task.",
  hourReportArchivedProjectCannotReportHours: "Hours cannot be reported to an archived project.",
  hourReportRejectedProjectCannotReportHours: "Hours cannot be reported to a rejected project.",
  hourReportCompletedProjectCannotReportHours: "Hours cannot be reported to a completed project.",
  hourReportDayCannotReportNewHours: "New hours cannot be reported at this day audit status.",
  hourReportUseCaseRequestReadyForCheckRequiresDraft: "Only draft hour reports can be requested ready for check.",
  hourReportUseCaseRequestReadyForApprovalRequiresReadyForCheck:
    "Only hour reports ready for check can be requested ready for approval.",
  hourReportUseCaseRequestReadyForApprovalCannotRequestSelf:
    "You are the only checker assigned to this report, so you cannot request a ready for approval reminder from yourself.",
  hourReportUseCaseMissingTaskParentProject: "Failed to load the parent project of the task",
  hourReportUseCaseForbidden: "You are not authorized to perform this action.",
}

type Translations = typeof defaultTranslations

const useTranslations = (): Translations => {
  const { t } = useTranslation("hour-report")
  const { t: tErrors } = useTranslation("errors")

  return {
    allReadyForReview: t("feedback.allReadyForReview", {
      defaultValue: defaultTranslations.allReadyForReview,
    }),
    allReadyForApproval: t("feedback.allReadyForApproval", {
      defaultValue: defaultTranslations.allReadyForApproval,
    }),
    checkerNotified: t("feedback.checkerNotified", {
      defaultValue: defaultTranslations.checkerNotified,
    }),
    approverNotified: t("feedback.approverNotified", {
      defaultValue: defaultTranslations.approverNotified,
    }),
    hourReportCreationFailed: tErrors("hourReportCreationFailed", {
      defaultValue: defaultTranslations.hourReportCreationFailed,
    }),
    hourReportSaved: t("feedback.hourReportSaved", {
      defaultValue: defaultTranslations.hourReportSaved,
    }),
    hourReportDeleteSuccess: t("feedback.hourReportDeleteSuccess", {
      defaultValue: defaultTranslations.hourReportDeleteSuccess,
    }),
    hourReportInvalidStateTransition: tErrors("hourReportInvalidStateTransition", {
      defaultValue: defaultTranslations.hourReportInvalidStateTransition,
    }),
    hourReportTaskAndProjectMismatch: tErrors("hourReportTaskAndProjectMismatch", {
      defaultValue: defaultTranslations.hourReportTaskAndProjectMismatch,
    }),
    hourReportUnknownMethod: tErrors("hourReportUnknownMethod", {
      defaultValue: defaultTranslations.hourReportUnknownMethod,
    }),
    hourReportInvalidFieldUpdate: tErrors("hourReportInvalidFieldUpdate", {
      defaultValue: defaultTranslations.hourReportInvalidFieldUpdate,
    }),
    hourReportHoursUpdateRequiresManualReport: tErrors("hourReportHoursUpdateRequiresManualReport", {
      defaultValue: defaultTranslations.hourReportHoursUpdateRequiresManualReport,
    }),
    hourReportTimeRangeUpdateRequiresTimedReport: tErrors("hourReportTimeRangeUpdateRequiresTimedReport", {
      defaultValue: defaultTranslations.hourReportTimeRangeUpdateRequiresTimedReport,
    }),
    hourReportTaskAssignmentRequiresProject: tErrors("hourReportTaskAssignmentRequiresProject", {
      defaultValue: defaultTranslations.hourReportTaskAssignmentRequiresProject,
    }),
    hourReportReportAlreadyReadyCheck: tErrors("hourReportReportAlreadyReadyCheck", {
      defaultValue: defaultTranslations.hourReportReportAlreadyReadyCheck,
    }),
    hourReportReportReadyApprovalCannotMoveToReadyCheck: tErrors(
      "hourReportReportReadyApprovalCannotMoveToReadyCheck",
      {
        defaultValue: defaultTranslations.hourReportReportReadyApprovalCannotMoveToReadyCheck,
      }
    ),
    hourReportReportApprovedCannotMoveToReadyCheck: tErrors("hourReportReportApprovedCannotMoveToReadyCheck", {
      defaultValue: defaultTranslations.hourReportReportApprovedCannotMoveToReadyCheck,
    }),
    hourReportReportAlreadyDraft: tErrors("hourReportReportAlreadyDraft", {
      defaultValue: defaultTranslations.hourReportReportAlreadyDraft,
    }),
    hourReportReportReadyApprovalCannotMoveToDraft: tErrors("hourReportReportReadyApprovalCannotMoveToDraft", {
      defaultValue: defaultTranslations.hourReportReportReadyApprovalCannotMoveToDraft,
    }),
    hourReportReportApprovedCannotMoveToDraft: tErrors("hourReportReportApprovedCannotMoveToDraft", {
      defaultValue: defaultTranslations.hourReportReportApprovedCannotMoveToDraft,
    }),
    hourReportReportDraftCannotBeChecked: tErrors("hourReportReportDraftCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportReportDraftCannotBeChecked,
    }),
    hourReportReportApprovedCannotBeChecked: tErrors("hourReportReportApprovedCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportReportApprovedCannotBeChecked,
    }),
    hourReportReportDraftCannotBeUnchecked: tErrors("hourReportReportDraftCannotBeUnchecked", {
      defaultValue: defaultTranslations.hourReportReportDraftCannotBeUnchecked,
    }),
    hourReportReportReadyCheckCannotBeUnchecked: tErrors("hourReportReportReadyCheckCannotBeUnchecked", {
      defaultValue: defaultTranslations.hourReportReportReadyCheckCannotBeUnchecked,
    }),
    hourReportReportApprovedCannotBeUnchecked: tErrors("hourReportReportApprovedCannotBeUnchecked", {
      defaultValue: defaultTranslations.hourReportReportApprovedCannotBeUnchecked,
    }),
    hourReportReportDraftCannotBeApproved: tErrors("hourReportReportDraftCannotBeApproved", {
      defaultValue: defaultTranslations.hourReportReportDraftCannotBeApproved,
    }),
    hourReportReportReadyCheckCannotBeApproved: tErrors("hourReportReportReadyCheckCannotBeApproved", {
      defaultValue: defaultTranslations.hourReportReportReadyCheckCannotBeApproved,
    }),
    hourReportReportAlreadyApproved: tErrors("hourReportReportAlreadyApproved", {
      defaultValue: defaultTranslations.hourReportReportAlreadyApproved,
    }),
    hourReportReportDraftCannotBeUnapproved: tErrors("hourReportReportDraftCannotBeUnapproved", {
      defaultValue: defaultTranslations.hourReportReportDraftCannotBeUnapproved,
    }),
    hourReportReportReadyCheckCannotBeUnapproved: tErrors("hourReportReportReadyCheckCannotBeUnapproved", {
      defaultValue: defaultTranslations.hourReportReportReadyCheckCannotBeUnapproved,
    }),
    hourReportReportReadyApprovalCannotBeUnapproved: tErrors("hourReportReportReadyApprovalCannotBeUnapproved", {
      defaultValue: defaultTranslations.hourReportReportReadyApprovalCannotBeUnapproved,
    }),
    hourReportDayAlreadyReadyCheck: tErrors("hourReportDayAlreadyReadyCheck", {
      defaultValue: defaultTranslations.hourReportDayAlreadyReadyCheck,
    }),
    hourReportDayReadyApprovalCannotMoveToReadyCheck: tErrors("hourReportDayReadyApprovalCannotMoveToReadyCheck", {
      defaultValue: defaultTranslations.hourReportDayReadyApprovalCannotMoveToReadyCheck,
    }),
    hourReportDayApprovedCannotMoveToReadyCheck: tErrors("hourReportDayApprovedCannotMoveToReadyCheck", {
      defaultValue: defaultTranslations.hourReportDayApprovedCannotMoveToReadyCheck,
    }),
    hourReportDayAlreadyNotReadyCheck: tErrors("hourReportDayAlreadyNotReadyCheck", {
      defaultValue: defaultTranslations.hourReportDayAlreadyNotReadyCheck,
    }),
    hourReportDayReadyApprovalCannotMoveToNotReadyCheck: tErrors(
      "hourReportDayReadyApprovalCannotMoveToNotReadyCheck",
      {
        defaultValue: defaultTranslations.hourReportDayReadyApprovalCannotMoveToNotReadyCheck,
      }
    ),
    hourReportDayApprovedCannotMoveToNotReadyCheck: tErrors("hourReportDayApprovedCannotMoveToNotReadyCheck", {
      defaultValue: defaultTranslations.hourReportDayApprovedCannotMoveToNotReadyCheck,
    }),
    hourReportDayNotReadyCheckCannotBeChecked: tErrors("hourReportDayNotReadyCheckCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportDayNotReadyCheckCannotBeChecked,
    }),
    hourReportDayApprovedCannotBeChecked: tErrors("hourReportDayApprovedCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportDayApprovedCannotBeChecked,
    }),
    hourReportDayNotReadyCheckCannotBeUnchecked: tErrors("hourReportDayNotReadyCheckCannotBeUnchecked", {
      defaultValue: defaultTranslations.hourReportDayNotReadyCheckCannotBeUnchecked,
    }),
    hourReportDayReadyCheckCannotBeUnchecked: tErrors("hourReportDayReadyCheckCannotBeUnchecked", {
      defaultValue: defaultTranslations.hourReportDayReadyCheckCannotBeUnchecked,
    }),
    hourReportDayApprovedCannotBeUnchecked: tErrors("hourReportDayApprovedCannotBeUnchecked", {
      defaultValue: defaultTranslations.hourReportDayApprovedCannotBeUnchecked,
    }),
    hourReportDayNotReadyCheckCannotBeApproved: tErrors("hourReportDayNotReadyCheckCannotBeApproved", {
      defaultValue: defaultTranslations.hourReportDayNotReadyCheckCannotBeApproved,
    }),
    hourReportDayReadyCheckCannotBeApproved: tErrors("hourReportDayReadyCheckCannotBeApproved", {
      defaultValue: defaultTranslations.hourReportDayReadyCheckCannotBeApproved,
    }),
    hourReportDayAlreadyApproved: tErrors("hourReportDayAlreadyApproved", {
      defaultValue: defaultTranslations.hourReportDayAlreadyApproved,
    }),
    hourReportDayNotReadyCheckCannotBeUnapproved: tErrors("hourReportDayNotReadyCheckCannotBeUnapproved", {
      defaultValue: defaultTranslations.hourReportDayNotReadyCheckCannotBeUnapproved,
    }),
    hourReportDayReadyCheckCannotBeUnapproved: tErrors("hourReportDayReadyCheckCannotBeUnapproved", {
      defaultValue: defaultTranslations.hourReportDayReadyCheckCannotBeUnapproved,
    }),
    hourReportDayReadyApprovalCannotBeUnapproved: tErrors("hourReportDayReadyApprovalCannotBeUnapproved", {
      defaultValue: defaultTranslations.hourReportDayReadyApprovalCannotBeUnapproved,
    }),
    hourReportUseCaseInvalidAction: tErrors("hourReportUseCaseInvalidAction", {
      defaultValue: defaultTranslations.hourReportUseCaseInvalidAction,
    }),
    hourReportUseCaseTaskProjectMismatch: tErrors("hourReportUseCaseTaskProjectMismatch", {
      defaultValue: defaultTranslations.hourReportUseCaseTaskProjectMismatch,
    }),
    hourReportUseCaseDraftReportCannotBeChecked: tErrors("hourReportUseCaseDraftReportCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportUseCaseDraftReportCannotBeChecked,
    }),
    hourReportUseCaseApprovedReportCannotBeChecked: tErrors("hourReportUseCaseApprovedReportCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportUseCaseApprovedReportCannotBeChecked,
    }),
    hourReportUseCaseDraftReportCannotBeEditedDuringApproval: tErrors(
      "hourReportUseCaseDraftReportCannotBeEditedDuringApproval",
      {
        defaultValue: defaultTranslations.hourReportUseCaseDraftReportCannotBeEditedDuringApproval,
      }
    ),
    hourReportUseCaseReadyCheckReportCannotBeEditedDuringApproval: tErrors(
      "hourReportUseCaseReadyCheckReportCannotBeEditedDuringApproval",
      {
        defaultValue: defaultTranslations.hourReportUseCaseReadyCheckReportCannotBeEditedDuringApproval,
      }
    ),
    hourReportUseCaseArchivedTaskCannotReportHours: tErrors("hourReportUseCaseArchivedTaskCannotReportHours", {
      defaultValue: defaultTranslations.hourReportUseCaseArchivedTaskCannotReportHours,
    }),
    hourReportUseCaseRejectedTaskCannotReportHours: tErrors("hourReportUseCaseRejectedTaskCannotReportHours", {
      defaultValue: defaultTranslations.hourReportUseCaseRejectedTaskCannotReportHours,
    }),
    hourReportUseCaseArchivedProjectCannotReportHours: tErrors("hourReportUseCaseArchivedProjectCannotReportHours", {
      defaultValue: defaultTranslations.hourReportUseCaseArchivedProjectCannotReportHours,
    }),
    hourReportUseCaseRejectedProjectCannotReportHours: tErrors("hourReportUseCaseRejectedProjectCannotReportHours", {
      defaultValue: defaultTranslations.hourReportUseCaseRejectedProjectCannotReportHours,
    }),
    hourReportUseCaseCheckConflict: tErrors("hourReportUseCaseCheckConflict", {
      defaultValue: defaultTranslations.hourReportUseCaseCheckConflict,
    }),
    hourReportServiceUserRequired: tErrors("hourReportServiceUserRequired", {
      defaultValue: defaultTranslations.hourReportServiceUserRequired,
    }),
    hourReportServiceUserProjectExpected: tErrors("hourReportServiceUserProjectExpected", {
      defaultValue: defaultTranslations.hourReportServiceUserProjectExpected,
    }),
    hourReportServiceUserTaskExpected: tErrors("hourReportServiceUserTaskExpected", {
      defaultValue: defaultTranslations.hourReportServiceUserTaskExpected,
    }),
    hourReportServiceMembershipExpected: tErrors("hourReportServiceMembershipExpected", {
      defaultValue: defaultTranslations.hourReportServiceMembershipExpected,
    }),
    hourReportServiceOrgProjectExpected: tErrors("hourReportServiceOrgProjectExpected", {
      defaultValue: defaultTranslations.hourReportServiceOrgProjectExpected,
    }),
    hourReportServiceOrgTaskExpected: tErrors("hourReportServiceOrgTaskExpected", {
      defaultValue: defaultTranslations.hourReportServiceOrgTaskExpected,
    }),
    hourReportServiceStrategyNotFound: tErrors("hourReportServiceStrategyNotFound", {
      defaultValue: defaultTranslations.hourReportServiceStrategyNotFound,
    }),
    hourReportServiceInvalidAuthRequestType: tErrors("hourReportServiceInvalidAuthRequestType", {
      defaultValue: defaultTranslations.hourReportServiceInvalidAuthRequestType,
    }),
    hourReportServiceContextDataMissing: tErrors("hourReportServiceContextDataMissing", {
      defaultValue: defaultTranslations.hourReportServiceContextDataMissing,
    }),
    hourReportReporterCannotChangeReadyForCheckHourReport: tErrors(
      "hourReportReporterCannotChangeReadyForCheckHourReport",
      {
        defaultValue: defaultTranslations.hourReportReporterCannotChangeReadyForCheckHourReport,
      }
    ),
    hourReportReporterCannotChangeCheckedHourReport: tErrors("hourReportReporterCannotChangeCheckedHourReport", {
      defaultValue: defaultTranslations.hourReportReporterCannotChangeCheckedHourReport,
    }),
    hourReportReporterCannotChangeApprovedHourReport: tErrors("hourReportReporterCannotChangeApprovedHourReport", {
      defaultValue: defaultTranslations.hourReportReporterCannotChangeApprovedHourReport,
    }),
    hourReportReporterCannotDelete: tErrors("hourReportReporterCannotDelete", {
      defaultValue: defaultTranslations.hourReportReporterCannotDelete,
    }),
    hourReportDraftCannotBeChecked: tErrors("hourReportDraftCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportDraftCannotBeChecked,
    }),
    hourReportApprovedCannotBeChecked: tErrors("hourReportApprovedCannotBeChecked", {
      defaultValue: defaultTranslations.hourReportApprovedCannotBeChecked,
    }),
    hourReportDraftCannotBeEditedDuringApproval: tErrors("hourReportDraftCannotBeEditedDuringApproval", {
      defaultValue: defaultTranslations.hourReportDraftCannotBeEditedDuringApproval,
    }),
    hourReportReadyCheckCannotBeEditedDuringApproval: tErrors("hourReportReadyCheckCannotBeEditedDuringApproval", {
      defaultValue: defaultTranslations.hourReportReadyCheckCannotBeEditedDuringApproval,
    }),
    hourReportReadyApprovalRequestRequiresReadyForCheck: tErrors(
      "hourReportReadyApprovalRequestRequiresReadyForCheck",
      {
        defaultValue: defaultTranslations.hourReportReadyApprovalRequestRequiresReadyForCheck,
      }
    ),
    hourReportArchivedTaskCannotReportHours: tErrors("hourReportArchivedTaskCannotReportHours", {
      defaultValue: defaultTranslations.hourReportArchivedTaskCannotReportHours,
    }),
    hourReportRejectedTaskCannotReportHours: tErrors("hourReportRejectedTaskCannotReportHours", {
      defaultValue: defaultTranslations.hourReportRejectedTaskCannotReportHours,
    }),
    hourReportCompletedTaskCannotReportHours: tErrors("hourReportCompletedTaskCannotReportHours", {
      defaultValue: defaultTranslations.hourReportCompletedTaskCannotReportHours,
    }),
    hourReportArchivedProjectCannotReportHours: tErrors("hourReportArchivedProjectCannotReportHours", {
      defaultValue: defaultTranslations.hourReportArchivedProjectCannotReportHours,
    }),
    hourReportRejectedProjectCannotReportHours: tErrors("hourReportRejectedProjectCannotReportHours", {
      defaultValue: defaultTranslations.hourReportRejectedProjectCannotReportHours,
    }),
    hourReportCompletedProjectCannotReportHours: tErrors("hourReportCompletedProjectCannotReportHours", {
      defaultValue: defaultTranslations.hourReportCompletedProjectCannotReportHours,
    }),
    hourReportDayCannotReportNewHours: tErrors("hourReportDayCannotReportNewHours", {
      defaultValue: defaultTranslations.hourReportDayCannotReportNewHours,
    }),
    hourReportUseCaseRequestReadyForCheckRequiresDraft: tErrors("hourReportUseCaseRequestReadyForCheckRequiresDraft", {
      defaultValue: defaultTranslations.hourReportUseCaseRequestReadyForCheckRequiresDraft,
    }),
    hourReportUseCaseRequestReadyForApprovalRequiresReadyForCheck: tErrors(
      "hourReportUseCaseRequestReadyForApprovalRequiresReadyForCheck",
      {
        defaultValue: defaultTranslations.hourReportUseCaseRequestReadyForApprovalRequiresReadyForCheck,
      }
    ),
    hourReportUseCaseRequestReadyForApprovalCannotRequestSelf: tErrors(
      "hourReportUseCaseRequestReadyForApprovalCannotRequestSelf",
      {
        defaultValue: defaultTranslations.hourReportUseCaseRequestReadyForApprovalCannotRequestSelf,
      }
    ),
    hourReportUseCaseMissingTaskParentProject: tErrors("hourReportUseCaseMissingTaskParentProject", {
      defaultValue: defaultTranslations.hourReportUseCaseMissingTaskParentProject,
    }),
    hourReportUseCaseForbidden: tErrors("hourReportUseCaseForbidden", {
      defaultValue: defaultTranslations.hourReportUseCaseForbidden,
    }),
  }
}
